// ============================================================
// verticalConfig.js — Per-vertical Cockpit copy
// ============================================================
// Every /green-shield · /uprising · /moto · /sims route renders the
// SAME 6-module Cockpit chassis. All industry-specific copy — brand
// name, trade nouns/verbs, sample jobs, invoices, client files, and
// weekly content ideas — lives here. Add a vertical by appending an
// entry to VERTICAL_CONFIG. Route file stays a thin wrapper.
// ============================================================

// Shared 7-day + 2-slot booking window across all verticals.
export const BOOKING_DAYS = [
  { key: "mon", label: "Mon", date: "Feb 03" },
  { key: "tue", label: "Tue", date: "Feb 04" },
  { key: "wed", label: "Wed", date: "Feb 05" },
  { key: "thu", label: "Thu", date: "Feb 06" },
  { key: "fri", label: "Fri", date: "Feb 07" },
  { key: "sat", label: "Sat", date: "Feb 08" },
  { key: "sun", label: "Sun", date: "Feb 09" },
];

export const BOOKING_INITIAL_GRID = {
  "mon-am": "crewA", "mon-pm": "open",
  "tue-am": "open",  "tue-pm": "crewB",
  "wed-am": "crewA", "wed-pm": "crewA",
  "thu-am": "open",  "thu-pm": "open",
  "fri-am": "crewB", "fri-pm": "open",
  "sat-am": "crewA", "sat-pm": "crewB",
  "sun-am": "open",  "sun-pm": "open",
};

// Time blocks shared across every Command Calendar.
export const TIME_BLOCKS = ["8am", "10am", "12pm", "2pm", "4pm"];

// ============================================================
// VERTICAL_CONFIG · four industries, one chassis.
// ============================================================
export const VERTICAL_CONFIG = {
  // ---------------------------------------------------------
  // LAWN · Dream Life Lawn — original reference implementation
  // ---------------------------------------------------------
  LAWN: {
    key: "LAWN",
    brand: "Dream Life Lawn",
    location: "Columbus, OH",
    trade: "Lawn Care",
    hero: {
      kicker: "Dream Life Lawn · Founder Greeting",
      subject: "yard business",
    },
    brandStripe: {
      title:
        "A lawn care operation running on the Grid — every call captured, every route filed,",
      titleTail: "every homeowner remembered.",
    },
    intakeShield: {
      kicker: "Module 01 · Intake Shield",
      title: "Every homeowner captured. Every callback confirmed.",
      blurb:
        "Amanda answers every inbound lawn service call — books the visit, logs the yard address, or schedules a callback if you're on the mower.",
      booking: {
        kicker: "Book a Mow",
        title: "The Booking Calendar",
        blurb:
          "Tap any green slot to request that mow window. Amanda locks it in and texts a confirmation the second you send.",
        slotAction: "Book This Slot",
        crewALabel: "Crew A",
        crewBLabel: "Crew B",
      },
      callMeLater: {
        kicker: "No-Miss Guarantee",
        title: "Call Me Later",
        idleCopy:
          "Homeowner can't talk right now? Tap the button. Amanda picks the moment, sends a text, and calls back on their window — not hers.",
        contactLabel: "Homeowner Phone (Amanda texts the confirmation)",
        brandLine: "Dream Life Lawn · Amanda:",
        callFrom: "(614) 555-0199",
      },
      proof: {
        title: "Yesterday · Amanda's Watch",
        stats: [
          { k: "Calls captured", v: "27" },
          { k: "Callbacks won",  v: "19" },
          { k: "Lost leads",     v: "0" },
        ],
      },
    },
    commandCalendar: {
      kicker: "Module 02 · Command Calendar",
      title: "Live dispatch board — every yard, every crew, every day.",
      blurb:
        "Amanda plots the routes overnight. Crews open the app at 7am and every stop is already loaded — address, service, and gate codes on file.",
      addAction: "Schedule Job",
      newLabel: "New Dispatch",
      addrLabel: "Yard Address",
      addrPlaceholder: "1234 Any St · Neighborhood",
      svcLabel: "Service",
      svcPlaceholder: "Mow & Edge",
      footerCount: (n) => `${n} jobs dispatched · this week`,
      jobs: [
        { id: "j1",  day: "mon", time: "8am",  crew: "A", addr: "2841 Maplewood Ave · Clintonville", svc: "Mow & Edge" },
        { id: "j2",  day: "mon", time: "12pm", crew: "B", addr: "704 S High St · German Village",    svc: "Bed Weeding + Mulch" },
        { id: "j3",  day: "tue", time: "10am", crew: "A", addr: "1189 Oakland Park Ave · Columbus",  svc: "Full Cleanup + Haul" },
        { id: "j4",  day: "tue", time: "2pm",  crew: "B", addr: "3502 Indianola Ave · Beechwold",    svc: "Aeration + Overseed" },
        { id: "j5",  day: "wed", time: "8am",  crew: "A", addr: "618 Kelton Ave · Bexley",           svc: "Mow & Edge" },
        { id: "j6",  day: "wed", time: "12pm", crew: "B", addr: "2110 W Third Ave · Grandview",      svc: "Mow + Trim Hedges" },
        { id: "j7",  day: "thu", time: "10am", crew: "A", addr: "455 E Beck St · Merion Village",    svc: "Full Cleanup" },
        { id: "j8",  day: "thu", time: "4pm",  crew: "B", addr: "1927 Neil Ave · University Dist.",  svc: "Mow & Edge" },
        { id: "j9",  day: "fri", time: "8am",  crew: "A", addr: "3308 N High St · Clintonville",     svc: "Mow + Fertilize" },
        { id: "j10", day: "fri", time: "2pm",  crew: "B", addr: "877 S Champion Ave · Old Oaks",     svc: "Mow & Edge" },
      ],
    },
    spine: {
      kicker: "Module 03 · Utility Spine",
      title: "Every yard, every quirk, remembered.",
      blurb:
        "Search 'back gate' or 'rose bed' across every client on file. Crews add notes voice-to-text from the mower. Nothing gets forgotten between visits.",
      addAction: "Add Note",
      newFileLabel: "New Client File",
      entityLabel: "Client Name",
      addrLabel: "Yard Address",
      counterLabel: "Client files",
      files: [
        { id: "c1", name: "Chen Family",       addr: "2841 Maplewood Ave · Clintonville",     last: "Jan 27 · Mow & Edge",         next: "Feb 03 · Mow & Edge",           note: "Double-cut on the back stretch — back gate sticks, use side entrance." },
        { id: "c2", name: "Whitlock, Marcus",  addr: "704 S High St · German Village",        last: "Jan 24 · Bed Cleanup",        next: "Feb 04 · Bed Weeding + Mulch",  note: "No edging near rose beds — trim by hand only. Homeowner is retired, chats a lot." },
        { id: "c3", name: "Rivera, Elena",     addr: "1189 Oakland Park Ave · Columbus",      last: "Jan 20 · Full Cleanup",       next: "Feb 03 · Full Cleanup + Haul",  note: "Two dogs — always ring the doorbell before opening the gate. Water bowl by the shed." },
        { id: "c4", name: "Byrne, Patrick",    addr: "3502 Indianola Ave · Beechwold",        last: "Oct 12 · Aeration",           next: "Feb 04 · Aeration + Overseed",  note: "Slope on the south side — use walk-behind, not the rider. Sprinkler heads on the east border." },
        { id: "c5", name: "Osei, Ama",         addr: "618 Kelton Ave · Bexley",               last: "Jan 28 · Leaf Removal",       next: "Feb 05 · Leaf Removal",         note: "Composts everything — leave leaf bags at the far corner of the driveway, NOT curbside." },
      ],
    },
    billing: {
      kicker: "Module 04 · Billing Autopilot",
      title: "Job complete → Invoice sent. One button.",
      blurb:
        "The moment the crew rolls off the yard, tap Send Invoice. Amanda sends the bill, texts the homeowner a receipt link, and files the job in the Grid.",
      entityLabel: "Homeowner",
      openLabel: "Jobs waiting",
      invoicedLabel: "Invoiced today",
      billedLabel: "Billed today",
      invoices: [
        { id: "inv1", addr: "2841 Maplewood Ave · Clintonville",  svc: "Mow & Edge",           crew: "Crew A", date: "Feb 03", price: 68  },
        { id: "inv2", addr: "1189 Oakland Park Ave · Columbus",   svc: "Full Cleanup + Haul",  crew: "Crew B", date: "Feb 03", price: 340 },
        { id: "inv3", addr: "704 S High St · German Village",     svc: "Bed Weeding + Mulch",  crew: "Crew A", date: "Feb 04", price: 215 },
        { id: "inv4", addr: "3502 Indianola Ave · Beechwold",     svc: "Aeration + Overseed",  crew: "Crew B", date: "Feb 04", price: 285 },
        { id: "inv5", addr: "618 Kelton Ave · Bexley",            svc: "Leaf Removal",         crew: "Crew A", date: "Feb 05", price: 175 },
      ],
    },
    growth: {
      kicker: "Module 05 · Growth Engine",
      title: "Ten videos a week — Amanda drafts them all.",
      blurb:
        "Amanda monitors local trends to find high-engagement themes for your weekly shoots. Before/afters, crew spotlights, seasonal tips, and testimonials — auto-drafted, scheduled, and posted.",
      pill: "10 High-Quality Strategic Assets/Week",
      videos: [
        { day: "Mon", slot: "AM", type: "Before/After",   title: "Overgrown Backyard → Golf Green" },
        { day: "Mon", slot: "PM", type: "Seasonal Tip",   title: "When to Start Overseeding in Ohio" },
        { day: "Tue", slot: "AM", type: "Crew Spotlight", title: "Meet Marcus · Lead on Crew A" },
        { day: "Wed", slot: "AM", type: "Customer Story", title: "The Chen Family · 3 Years on the Grid" },
        { day: "Wed", slot: "PM", type: "Before/After",   title: "Front Yard Bed Rebuild · Clintonville" },
        { day: "Thu", slot: "AM", type: "Quick Tip",      title: "60-Second Mulch Depth Trick" },
        { day: "Fri", slot: "AM", type: "Timelapse",      title: "Full Cleanup · Oakland Park Ave" },
        { day: "Fri", slot: "PM", type: "Team Reel",      title: "This Week on the Yards" },
        { day: "Sat", slot: "AM", type: "Seasonal Push",  title: "Book Your Fall Aeration · 3 Spots Left" },
        { day: "Sun", slot: "PM", type: "Testimonial",    title: "Neighbor of the Month · Bexley" },
      ],
    },
  },

  // ---------------------------------------------------------
  // MUSIC · The Uprising — band / backstage pass
  // ---------------------------------------------------------
  MUSIC: {
    key: "MUSIC",
    brand: "The Uprising",
    location: "Columbus, OH · Rock Circuit",
    trade: "Live Music",
    hero: {
      kicker: "The Uprising · Founder Greeting",
      subject: "band business",
    },
    brandStripe: {
      title:
        "A rock band running on the Grid — every fan captured, every show filed,",
      titleTail: "every date sold out.",
    },
    intakeShield: {
      kicker: "Module 01 · Intake Shield",
      title: "Every fan captured. Every backstage request routed.",
      blurb:
        "Amanda answers every inbound fan call and DM — books the meet-and-greet slot, logs the venue, or schedules a callback if the band is on the road.",
      booking: {
        kicker: "Book a Meet-and-Greet",
        title: "The Backstage Calendar",
        blurb:
          "Tap any green slot to request a backstage window. Amanda locks it in and texts a QR pass the second you send.",
        slotAction: "Grab This Slot",
        crewALabel: "Front House",
        crewBLabel: "Backstage",
      },
      callMeLater: {
        kicker: "No-Miss Guarantee",
        title: "Call Me Later",
        idleCopy:
          "Fan can't talk during soundcheck? Tap the button. Amanda picks the moment, sends a text, and calls back on their window — not yours.",
        contactLabel: "Fan Phone (Amanda texts the QR pass)",
        brandLine: "The Uprising · Amanda:",
        callFrom: "(614) 555-0142",
      },
      proof: {
        title: "Yesterday · Amanda's Watch",
        stats: [
          { k: "Fan calls captured",  v: "84" },
          { k: "Backstage confirmed", v: "22" },
          { k: "Lost DMs",            v: "0" },
        ],
      },
    },
    commandCalendar: {
      kicker: "Module 02 · Command Calendar",
      title: "Live tour board — every date, every crew, every load-in.",
      blurb:
        "Amanda plots the tour overnight. The band opens the app at noon and every stop is already loaded — venue, set-time, and green-room notes on file.",
      addAction: "Add Show",
      newLabel: "New Show",
      addrLabel: "Venue",
      addrPlaceholder: "The King of Clubs · Columbus, OH",
      svcLabel: "Set Type",
      svcPlaceholder: "Headline · 75 min",
      footerCount: (n) => `${n} shows on the run`,
      jobs: [
        { id: "s1",  day: "mon", time: "4pm",  crew: "A", addr: "The King of Clubs · Columbus, OH",       svc: "Load-In · Sound Check" },
        { id: "s2",  day: "mon", time: "8am",  crew: "B", addr: "Rise Studios · Grandview",               svc: "Rehearsal · 3 hr" },
        { id: "s3",  day: "tue", time: "10am", crew: "A", addr: "The Basement · Columbus, OH",            svc: "Load-In · Full Rig" },
        { id: "s4",  day: "tue", time: "2pm",  crew: "B", addr: "WCBE 90.5 FM · Downtown",                svc: "Radio Session · Acoustic" },
        { id: "s5",  day: "wed", time: "8am",  crew: "A", addr: "Newport Music Hall · High St",           svc: "Set-Up · Opening Slot" },
        { id: "s6",  day: "wed", time: "12pm", crew: "B", addr: "The Bluestone · Uptown",                 svc: "Meet-and-Greet · 45 min" },
        { id: "s7",  day: "thu", time: "10am", crew: "A", addr: "Ace of Cups · North Campus",             svc: "Load-In · Co-Headline" },
        { id: "s8",  day: "thu", time: "4pm",  crew: "B", addr: "Nightlight 614 · Franklinton",           svc: "Late Set · 60 min" },
        { id: "s9",  day: "fri", time: "8am",  crew: "A", addr: "The A&R Music Bar · Arena District",     svc: "Album Preview Show" },
        { id: "s10", day: "fri", time: "2pm",  crew: "B", addr: "Kemba Live! · Amphitheater",             svc: "Festival Slot · 45 min" },
      ],
    },
    spine: {
      kicker: "Module 03 · Utility Spine",
      title: "Every fan, every venue, remembered.",
      blurb:
        "Search 'birthday show' or 'front row' across every fan on file. Crew adds notes voice-to-text from the tour van. Nothing gets forgotten between cities.",
      addAction: "Add Fan Note",
      newFileLabel: "New Fan File",
      entityLabel: "Fan Name",
      addrLabel: "Home City",
      counterLabel: "Fan files",
      files: [
        { id: "f1", name: "Reyes, Diana",      addr: "Columbus, OH · Superfan",                last: "Jan 24 · The Basement (VIP)",   next: "Feb 11 · King of Clubs",            note: "Front-row every Columbus date since 2022. Birthday show is Aug 11 — save a mainstage shout-out." },
        { id: "f2", name: "Kolar, Jake",       addr: "Cleveland, OH · Merch Buyer",            last: "Dec 09 · Beachland Ballroom",   next: "Mar 02 · Grog Shop",                note: "Buys every vinyl variant. Wants signed test-pressing of the next drop. Cash tips crew after every show." },
        { id: "f3", name: "Nguyen, Priya",     addr: "Cincinnati, OH · Guest List",            last: "Jan 17 · Bogart's",             next: "Mar 15 · MegaCorp Fest",            note: "Local promoter's kid — always +2 on the list. Loves the deep cut 'Wildflower Fire' — work it into the encore." },
        { id: "f4", name: "Okonkwo, Deon",     addr: "Detroit, MI · Tour Photog",              last: "Jan 12 · The Loving Touch",     next: "Feb 22 · Saint Andrew's Hall",      note: "Shoots every Michigan run — media pass approved by manager. Pull him in for the album trailer." },
        { id: "f5", name: "Shelton, Riley",    addr: "Pittsburgh, PA · Radio Contact",         last: "Nov 30 · Rex Theater",          next: "Feb 27 · Mr. Smalls Theatre",       note: "WYEP DJ. Playlist adds every single. Send early acoustic cut of the new record for exclusive first-spin." },
      ],
    },
    billing: {
      kicker: "Module 04 · Billing Autopilot",
      title: "Show settled → Payout sent. One button.",
      blurb:
        "The moment the load-out wraps, tap Send Invoice. Amanda settles the guarantee, texts the venue accountant, and files the payout in the Grid.",
      entityLabel: "Venue",
      openLabel: "Shows unsettled",
      invoicedLabel: "Settled today",
      billedLabel: "Paid today",
      invoices: [
        { id: "inv1", addr: "The King of Clubs · Columbus, OH",     svc: "Headline · 75 min + Merch Cut",  crew: "Front House", date: "Feb 03", price: 2400 },
        { id: "inv2", addr: "The Basement · Columbus, OH",          svc: "Co-Headline · 60 min",           crew: "Backstage",   date: "Feb 03", price: 1800 },
        { id: "inv3", addr: "Newport Music Hall · High St",         svc: "Opening Slot · 30 min",          crew: "Front House", date: "Feb 04", price: 850  },
        { id: "inv4", addr: "Ace of Cups · North Campus",           svc: "Co-Headline · 60 min",           crew: "Backstage",   date: "Feb 04", price: 1650 },
        { id: "inv5", addr: "Kemba Live! Amphitheater · Festival",  svc: "Festival Slot · 45 min",         crew: "Front House", date: "Feb 05", price: 3200 },
      ],
    },
    growth: {
      kicker: "Module 05 · Growth Engine",
      title: "Ten videos a week — Amanda drafts them all.",
      blurb:
        "Amanda tracks the local scene for high-engagement themes for your weekly drops. Backstage cuts, riff clips, tour timelapses, and fan spotlights — auto-drafted, scheduled, and posted.",
      pill: "10 High-Quality Strategic Assets/Week",
      videos: [
        { day: "Mon", slot: "AM", type: "Riff Clip",       title: "15-Second Hook · New Single Snippet" },
        { day: "Mon", slot: "PM", type: "Tour Announce",   title: "Spring Circuit Dates · Full Reveal" },
        { day: "Tue", slot: "AM", type: "Band Spotlight",  title: "Meet Jake · Lead Guitar Rig Tour" },
        { day: "Wed", slot: "AM", type: "Fan Story",       title: "Diana · Front Row Every Columbus Date" },
        { day: "Wed", slot: "PM", type: "Timelapse",       title: "Load-In → Show at The Basement" },
        { day: "Thu", slot: "AM", type: "Quick Tip",       title: "Why We Track Vocals Last · 60 Seconds" },
        { day: "Fri", slot: "AM", type: "Studio Cut",      title: "Behind the Board · New Track Rough" },
        { day: "Fri", slot: "PM", type: "Weekly Reel",     title: "This Week on the Road · Ohio Run" },
        { day: "Sat", slot: "AM", type: "Ticket Push",     title: "King of Clubs · Under 40 Left" },
        { day: "Sun", slot: "PM", type: "Testimonial",     title: "Fan of the Week · Cleveland" },
      ],
    },
  },

  // ---------------------------------------------------------
  // MOTO · Titan Motorcycle — auto/motorcycle repair shop
  // ---------------------------------------------------------
  MOTO: {
    key: "MOTO",
    brand: "Titan Motorcycle",
    location: "Columbus, OH",
    trade: "Motorcycle Service",
    hero: {
      kicker: "Titan Motorcycle · Founder Greeting",
      subject: "shop business",
    },
    brandStripe: {
      title:
        "A motorcycle shop running on the Grid — every service call captured, every bay filed,",
      titleTail: "every rider taken care of.",
    },
    intakeShield: {
      kicker: "Module 01 · Intake Shield",
      title: "Every rider captured. Every service slot confirmed.",
      blurb:
        "Amanda answers every inbound service call — books the bay window, logs the bike and VIN, or schedules a callback if the phone rings while the lift is up.",
      booking: {
        kicker: "Book a Bay",
        title: "The Service Calendar",
        blurb:
          "Tap any green slot to reserve that bay window. Amanda locks it in and texts a drop-off ticket the second you send.",
        slotAction: "Reserve This Bay",
        crewALabel: "Bay 1",
        crewBLabel: "Bay 2",
      },
      callMeLater: {
        kicker: "No-Miss Guarantee",
        title: "Call Me Later",
        idleCopy:
          "Rider can't talk mid-ride? Tap the button. Amanda picks the moment, sends a text, and calls back when they're off the bike — not before.",
        contactLabel: "Rider Phone (Amanda texts the drop-off ticket)",
        brandLine: "Titan Motorcycle · Amanda:",
        callFrom: "(614) 555-0177",
      },
      proof: {
        title: "Yesterday · Amanda's Watch",
        stats: [
          { k: "Service calls",   v: "31" },
          { k: "Bays booked",     v: "14" },
          { k: "Lost riders",     v: "0" },
        ],
      },
    },
    commandCalendar: {
      kicker: "Module 02 · Command Calendar",
      title: "Live bay board — every bike, every tech, every day.",
      blurb:
        "Amanda plots the shop overnight. Techs walk in at 8am and every lift is already staged — VIN, service, and rider notes on the ticket.",
      addAction: "Schedule Service",
      newLabel: "New Service Ticket",
      addrLabel: "Bike / VIN Tag",
      addrPlaceholder: "'19 Triumph Bonneville · VIN-4732",
      svcLabel: "Service Type",
      svcPlaceholder: "Oil Change + Chain Adjust",
      footerCount: (n) => `${n} tickets on the board`,
      jobs: [
        { id: "t1",  day: "mon", time: "8am",  crew: "A", addr: "'19 Triumph Bonneville · VIN-4732",    svc: "Oil Change + Chain Adjust" },
        { id: "t2",  day: "mon", time: "12pm", crew: "B", addr: "'21 Harley Road Glide · VIN-8891",     svc: "Brake Pad Swap · Front/Rear" },
        { id: "t3",  day: "tue", time: "10am", crew: "A", addr: "'22 Ducati Monster · VIN-3324",        svc: "60-Point Service Interval" },
        { id: "t4",  day: "tue", time: "2pm",  crew: "B", addr: "'17 BMW R1250GS · VIN-1102",           svc: "Valve Adjustment · Full" },
        { id: "t5",  day: "wed", time: "8am",  crew: "A", addr: "'23 Indian Chief · VIN-5518",          svc: "Break-In Service · 1000mi" },
        { id: "t6",  day: "wed", time: "12pm", crew: "B", addr: "'20 KTM 890 Duke · VIN-7761",          svc: "Tire Change · Front + Rear" },
        { id: "t7",  day: "thu", time: "10am", crew: "A", addr: "'18 Kawasaki Ninja 400 · VIN-2087",    svc: "Battery + Full Diagnostic" },
        { id: "t8",  day: "thu", time: "4pm",  crew: "B", addr: "'22 Honda Africa Twin · VIN-4499",     svc: "Suspension Tune · Öhlins" },
        { id: "t9",  day: "fri", time: "8am",  crew: "A", addr: "'16 Yamaha MT-09 · VIN-6653",          svc: "Clutch Rebuild + Fluid" },
        { id: "t10", day: "fri", time: "2pm",  crew: "B", addr: "'24 Aprilia Tuono · VIN-9910",         svc: "Winter Storage Prep" },
      ],
    },
    spine: {
      kicker: "Module 03 · Utility Spine",
      title: "Every bike, every quirk, remembered.",
      blurb:
        "Search 'left grip loose' or 'clunky third gear' across every ticket on file. Techs add notes voice-to-text from the bay. Nothing gets forgotten between visits.",
      addAction: "Add Rider Note",
      newFileLabel: "New Rider File",
      entityLabel: "Rider Name",
      addrLabel: "Bike / VIN",
      counterLabel: "Rider files",
      files: [
        { id: "r1", name: "Dawson, Cole",      addr: "'19 Triumph Bonneville · VIN-4732",  last: "Nov 04 · Oil + Chain",             next: "Feb 03 · Oil + Chain Adjust",         note: "Chain always runs a touch loose — customer likes it that way. Do NOT re-tension without checking with him first." },
        { id: "r2", name: "Vasquez, Iris",     addr: "'21 Harley Road Glide · VIN-8891",   last: "Aug 18 · Full Detail + Wax",       next: "Feb 03 · Brake Pad Swap",             note: "Cross-country in April — needs sound-proofing check before the run. Bags always come with her." },
        { id: "r3", name: "Nakamura, Kai",     addr: "'22 Ducati Monster · VIN-3324",      last: "Oct 22 · Valve Check",             next: "Feb 04 · 60-Point Service",           note: "Track day at Mid-Ohio Mar 15 — schedule pre-track inspection Mar 10. Prefers Michelin Power Cup 2." },
        { id: "r4", name: "Ellis, Tomás",      addr: "'17 BMW R1250GS · VIN-1102",         last: "Jul 07 · Suspension Overhaul",     next: "Feb 04 · Valve Adjustment",           note: "Adventure rig — always mud in the wheel wells, pressure-wash before delivery. Chatty, offers coffee every visit." },
        { id: "r5", name: "Brooks, Jenae",     addr: "'23 Indian Chief · VIN-5518",        last: "Jan 15 · 500mi Break-In",          next: "Feb 05 · 1000mi Break-In",            note: "New rider — walk her through everything replaced. Loves the sound, don't touch the pipes." },
      ],
    },
    billing: {
      kicker: "Module 04 · Billing Autopilot",
      title: "Bike out → Invoice sent. One button.",
      blurb:
        "The second the bike rolls out of the bay, tap Send Invoice. Amanda sends the ticket, texts the rider a pay-link, and files the service in the Grid.",
      entityLabel: "Rider",
      openLabel: "Tickets waiting",
      invoicedLabel: "Invoiced today",
      billedLabel: "Billed today",
      invoices: [
        { id: "inv1", addr: "'19 Triumph Bonneville · Dawson",     svc: "Oil Change + Chain Adjust",      crew: "Bay 1", date: "Feb 03", price: 145 },
        { id: "inv2", addr: "'21 Harley Road Glide · Vasquez",     svc: "Brake Pad Swap · Front/Rear",    crew: "Bay 2", date: "Feb 03", price: 480 },
        { id: "inv3", addr: "'22 Ducati Monster · Nakamura",       svc: "60-Point Service Interval",      crew: "Bay 1", date: "Feb 04", price: 620 },
        { id: "inv4", addr: "'17 BMW R1250GS · Ellis",             svc: "Valve Adjustment · Full",        crew: "Bay 2", date: "Feb 04", price: 890 },
        { id: "inv5", addr: "'23 Indian Chief · Brooks",           svc: "1000mi Break-In Service",        crew: "Bay 1", date: "Feb 05", price: 340 },
      ],
    },
    growth: {
      kicker: "Module 05 · Growth Engine",
      title: "Ten videos a week — Amanda drafts them all.",
      blurb:
        "Amanda tracks the local rider scene for high-engagement themes for your weekly shoots. Bike builds, tech spotlights, tire tips, and rider testimonials — auto-drafted, scheduled, and posted.",
      pill: "10 High-Quality Strategic Assets/Week",
      videos: [
        { day: "Mon", slot: "AM", type: "Before/After",   title: "Dented Tank → Fresh Paint · Bonneville" },
        { day: "Mon", slot: "PM", type: "Seasonal Tip",   title: "Cold-Start Prep · Ohio Winter Riding" },
        { day: "Tue", slot: "AM", type: "Tech Spotlight", title: "Meet Jax · Lead Tech on the Twins" },
        { day: "Wed", slot: "AM", type: "Rider Story",    title: "Iris · Cross-Country on the Road Glide" },
        { day: "Wed", slot: "PM", type: "Build Reel",     title: "Full Custom · '22 Ducati Track Bike" },
        { day: "Thu", slot: "AM", type: "Quick Tip",      title: "60-Second Tire Pressure Check" },
        { day: "Fri", slot: "AM", type: "Timelapse",      title: "Valve Adjust · '17 GS Overhaul" },
        { day: "Fri", slot: "PM", type: "Team Reel",      title: "This Week in the Bays" },
        { day: "Sat", slot: "AM", type: "Seasonal Push",  title: "Book Your Spring Service · 4 Bays Left" },
        { day: "Sun", slot: "PM", type: "Testimonial",    title: "Rider of the Month · Bexley" },
      ],
    },
  },

  // ---------------------------------------------------------
  // LOGISTICS · Sims Brothers — trucking / fleet dispatch
  // ---------------------------------------------------------
  LOGISTICS: {
    key: "LOGISTICS",
    brand: "Sims Brothers Recycling",
    location: "Marion, OH",
    trade: "Fleet Logistics",
    hero: {
      kicker: "Sims Brothers · Founder Greeting",
      subject: "freight business",
    },
    brandStripe: {
      title:
        "A logistics operation running on the Grid — every load captured, every route filed,",
      titleTail: "every driver on the clock.",
    },
    intakeShield: {
      kicker: "Module 01 · Intake Shield",
      title: "Every load captured. Every dispatch confirmed.",
      blurb:
        "Amanda answers every inbound broker call — books the pickup window, logs the origin and drop, or schedules a callback if the phone rings mid-run.",
      booking: {
        kicker: "Book a Pickup",
        title: "The Dispatch Calendar",
        blurb:
          "Tap any green slot to reserve a pickup window. Amanda locks the load in and texts a BOL to the driver the second you send.",
        slotAction: "Book This Run",
        crewALabel: "Fleet A",
        crewBLabel: "Fleet B",
      },
      callMeLater: {
        kicker: "No-Miss Guarantee",
        title: "Call Me Later",
        idleCopy:
          "Broker can't talk right now? Tap the button. Amanda picks the moment, sends a text, and calls back on their window — not yours.",
        contactLabel: "Broker Phone (Amanda texts the load ticket)",
        brandLine: "Sims Brothers · Amanda:",
        callFrom: "(740) 555-0116",
      },
      proof: {
        title: "Yesterday · Amanda's Watch",
        stats: [
          { k: "Broker calls",  v: "42" },
          { k: "Loads booked",  v: "18" },
          { k: "Deadhead miles", v: "0" },
        ],
      },
    },
    commandCalendar: {
      kicker: "Module 02 · Command Calendar",
      title: "Live dispatch board — every truck, every route, every day.",
      blurb:
        "Amanda plots the routes overnight. Drivers pre-trip at 6am and every stop is already loaded — pickup, drop, and gate codes on the BOL.",
      addAction: "Schedule Run",
      newLabel: "New Dispatch",
      addrLabel: "Pickup → Drop",
      addrPlaceholder: "Marion, OH → Columbus, OH",
      svcLabel: "Load Type",
      svcPlaceholder: "Steel · 42,000 lbs",
      footerCount: (n) => `${n} runs on the board`,
      jobs: [
        { id: "d1",  day: "mon", time: "8am",  crew: "A", addr: "Marion, OH → Columbus, OH",          svc: "Steel · 42,000 lbs" },
        { id: "d2",  day: "mon", time: "12pm", crew: "B", addr: "Wooster, OH → Cleveland, OH",         svc: "Copper · 38,000 lbs" },
        { id: "d3",  day: "tue", time: "10am", crew: "A", addr: "Delaware, OH → Columbus, OH",         svc: "Aluminium · 44,000 lbs" },
        { id: "d4",  day: "tue", time: "2pm",  crew: "B", addr: "Bucyrus, OH → Marion, OH",            svc: "Mixed Metal · 36,000 lbs" },
        { id: "d5",  day: "wed", time: "8am",  crew: "A", addr: "Ashland, OH → Wooster, OH",           svc: "Cardboard Bales · 24 pallets" },
        { id: "d6",  day: "wed", time: "12pm", crew: "B", addr: "Mansfield, OH → Columbus, OH",        svc: "Auto Bodies · 12 units" },
        { id: "d7",  day: "thu", time: "10am", crew: "A", addr: "Marion, OH → Toledo, OH",             svc: "Steel Coil · 40,000 lbs" },
        { id: "d8",  day: "thu", time: "4pm",  crew: "B", addr: "Zanesville, OH → Columbus, OH",       svc: "Plastic Regrind · 22 gaylords" },
        { id: "d9",  day: "fri", time: "8am",  crew: "A", addr: "Marion, OH → Cincinnati, OH",         svc: "Copper · 41,000 lbs" },
        { id: "d10", day: "fri", time: "2pm",  crew: "B", addr: "Findlay, OH → Marion, OH",            svc: "Aluminium Cans · 32 gaylords" },
      ],
    },
    spine: {
      kicker: "Module 03 · Utility Spine",
      title: "Every broker, every dock, remembered.",
      blurb:
        "Search 'liftgate needed' or 'gate closes at 4' across every load on file. Drivers add notes voice-to-text from the cab. Nothing gets forgotten between runs.",
      addAction: "Add Broker Note",
      newFileLabel: "New Broker File",
      entityLabel: "Broker / Shipper",
      addrLabel: "Primary Origin",
      counterLabel: "Broker files",
      files: [
        { id: "b1", name: "Continental Steel Co.",  addr: "Marion, OH · Origin dock 4",       last: "Jan 27 · 42k Steel to Columbus",  next: "Feb 03 · 42k Steel to Columbus",   note: "Dock 4 only — the north bays block trailer swing. Chain up in the pit, forklift will not tarp." },
        { id: "b2", name: "Midwest Copper Ltd.",    addr: "Wooster, OH · Bay 2",              last: "Jan 24 · 38k Copper to Cleveland", next: "Feb 03 · 38k Copper to Cleveland", note: "Broker Renee runs tight timing — arrive 15 min early or lose the window. Cash tips for early departure." },
        { id: "b3", name: "Ohio Auto Recyclers",    addr: "Delaware, OH · Yard C",            last: "Jan 22 · 12 units to Columbus",    next: "Feb 03 · 12 units to Columbus",    note: "Gate closes 4 pm sharp. Yard boss Ray needs BOL signed BEFORE loading — not after. No exceptions." },
        { id: "b4", name: "Buckeye Materials",      addr: "Bucyrus, OH · Weigh station east", last: "Jan 15 · 36k Mixed to Marion",     next: "Feb 04 · 36k Mixed to Marion",     note: "Scale runs +200 lb heavy after 2 pm — book morning slots. Weigh master Todd is our guy." },
        { id: "b5", name: "River Valley Fiber",     addr: "Ashland, OH · 3 loading bays",     last: "Jan 28 · 24 pallets to Wooster",   next: "Feb 05 · 24 pallets to Wooster",   note: "Composts everything wet — dry loads only. Liftgate REQUIRED for backhaul. Ask for Marisol at the desk." },
      ],
    },
    billing: {
      kicker: "Module 04 · Billing Autopilot",
      title: "Load delivered → Invoice sent. One button.",
      blurb:
        "The moment the driver hits deliver, tap Send Invoice. Amanda sends the freight bill, texts the broker a pay-link, and files the load in the Grid.",
      entityLabel: "Broker",
      openLabel: "Loads unsettled",
      invoicedLabel: "Invoiced today",
      billedLabel: "Billed today",
      invoices: [
        { id: "inv1", addr: "Marion → Columbus · 42k Steel",         svc: "One-Way · 54 mi",             crew: "Fleet A", date: "Feb 03", price: 1240 },
        { id: "inv2", addr: "Wooster → Cleveland · 38k Copper",      svc: "One-Way · 62 mi",             crew: "Fleet B", date: "Feb 03", price: 1580 },
        { id: "inv3", addr: "Delaware → Columbus · 12 Auto Units",   svc: "Round-Trip · 44 mi",          crew: "Fleet A", date: "Feb 04", price: 2100 },
        { id: "inv4", addr: "Bucyrus → Marion · 36k Mixed",          svc: "One-Way · 26 mi",             crew: "Fleet B", date: "Feb 04", price: 720  },
        { id: "inv5", addr: "Ashland → Wooster · 24 pallets Fiber",  svc: "One-Way · 38 mi · Liftgate",  crew: "Fleet A", date: "Feb 05", price: 985  },
      ],
    },
    growth: {
      kicker: "Module 05 · Growth Engine",
      title: "Ten videos a week — Amanda drafts them all.",
      blurb:
        "Amanda tracks the local freight scene for high-engagement themes for your weekly shoots. Driver spotlights, on-time stats, safety tips, and broker testimonials — auto-drafted, scheduled, and posted.",
      pill: "10 High-Quality Strategic Assets/Week",
      videos: [
        { day: "Mon", slot: "AM", type: "Driver Spotlight", title: "Meet Ramirez · Steel Runner Since '18" },
        { day: "Mon", slot: "PM", type: "Seasonal Tip",     title: "Pre-Trip Checklist · Ohio Winter Runs" },
        { day: "Tue", slot: "AM", type: "On-Time Reel",     title: "97% On-Time · Q1 Fleet Report" },
        { day: "Wed", slot: "AM", type: "Broker Story",     title: "Continental Steel · 8 Years on the Grid" },
        { day: "Wed", slot: "PM", type: "Timelapse",        title: "Load-In → Delivery · Marion to Columbus" },
        { day: "Thu", slot: "AM", type: "Quick Tip",        title: "60-Second Chain-Up · Steel Loads" },
        { day: "Fri", slot: "AM", type: "Route Reel",       title: "This Week on the Ohio Grid" },
        { day: "Fri", slot: "PM", type: "Team Reel",        title: "Yard Crew · Behind the Scale" },
        { day: "Sat", slot: "AM", type: "Capacity Push",    title: "Open Loads · This Weekend · 3 Trucks Free" },
        { day: "Sun", slot: "PM", type: "Testimonial",      title: "Broker of the Month · Buckeye Materials" },
      ],
    },
  },

  // ---------------------------------------------------------
  // MOBILE_CYCLE · Mobile Cycle Works, Inc. — vintage motorcycle
  // restoration. Ashley, OH. Walnut / leather / brass aesthetic.
  // Vocabulary MUST stay isolated from /moto (no "bay", no "VIN",
  // no "rider"). Vintage-only nouns: bench, serial, patina,
  // tank, carbs, chrome, owner, restoration.
  // ---------------------------------------------------------
  MOBILE_CYCLE: {
    key: "MOBILE_CYCLE",
    brand: "Mobile Cycle Works, Inc.",
    location: "Ashley, OH",
    trade: "Vintage Restoration",
    hero: {
      kicker: "Mobile Cycle Works · Founder Greeting",
      subject: "restoration bench",
    },
    brandStripe: {
      title:
        "A vintage restoration bench running on the Grid — every owner captured, every tank filed,",
      titleTail: "every serial remembered.",
    },
    intakeShield: {
      kicker: "Module 01 · Intake Shield",
      title: "Every owner captured. Every bench slot confirmed.",
      blurb:
        "Amanda answers every inbound restoration call — books the bench window, logs the machine and serial, or schedules a callback if you're wet-sanding a tank.",
      booking: {
        kicker: "Book a Bench",
        title: "The Restoration Calendar",
        blurb:
          "Tap any open slot to reserve a bench window. Amanda locks it in and texts a drop-off card the second you send.",
        slotAction: "Reserve This Bench",
        crewALabel: "Bench 1",
        crewBLabel: "Bench 2",
      },
      callMeLater: {
        kicker: "No-Miss Guarantee",
        title: "Call Me Later",
        idleCopy:
          "Owner can't talk while you're on the buffing wheel? Tap the button. Amanda picks the moment, sends a text, and calls back on their window — not yours.",
        contactLabel: "Owner Phone (Amanda texts the drop-off card)",
        brandLine: "Mobile Cycle Works · Amanda:",
        callFrom: "(740) 555-0138",
      },
      proof: {
        title: "Yesterday · Amanda's Watch",
        stats: [
          { k: "Restoration calls", v: "18" },
          { k: "Benches booked",    v: "9"  },
          { k: "Lost owners",       v: "0"  },
        ],
      },
    },
    commandCalendar: {
      kicker: "Module 02 · Command Calendar",
      title: "Live bench board — every machine, every hand, every day.",
      blurb:
        "Amanda plots the shop overnight. Restorers walk in at 8am and every bench is already staged — serial, work order, and owner notes on the card.",
      addAction: "Schedule Bench",
      newLabel: "New Bench Card",
      addrLabel: "Machine · Serial",
      addrPlaceholder: "'68 Triumph Bonneville · SN T120R-DU-84921",
      svcLabel: "Restoration Type",
      svcPlaceholder: "Tank re-paint · pinstripe",
      footerCount: (n) => `${n} bench cards on the board`,
      jobs: [
        { id: "mc1",  day: "mon", time: "8am",  crew: "A", addr: "'68 Triumph Bonneville · SN T120R-DU-84921",   svc: "Tank Re-Paint + Pinstripe" },
        { id: "mc2",  day: "mon", time: "12pm", crew: "B", addr: "'72 Norton Commando 750 · SN 220134",           svc: "Carb Rebuild · Pair Amal" },
        { id: "mc3",  day: "tue", time: "10am", crew: "A", addr: "'65 BSA Lightning Rocket · SN A65LR-2841",      svc: "Full Chrome Re-Plate" },
        { id: "mc4",  day: "tue", time: "2pm",  crew: "B", addr: "'76 Honda CB750 Four · SN CB750-2380118",       svc: "Wet Sand + Clear Coat Tank" },
        { id: "mc5",  day: "wed", time: "8am",  crew: "A", addr: "'59 Harley Panhead · SN 59FL-1104",             svc: "Bottom-End Rebuild" },
        { id: "mc6",  day: "wed", time: "12pm", crew: "B", addr: "'71 Ducati 750 GT · SN 750GT-751023",           svc: "Bevel-Drive Tune + Seals" },
        { id: "mc7",  day: "thu", time: "10am", crew: "A", addr: "'62 Vincent Rapide · SN F10AB-1B-8813",         svc: "Magneto Rewind" },
        { id: "mc8",  day: "thu", time: "4pm",  crew: "B", addr: "'78 Kawasaki KZ1000 · SN KZ1000A-42817",        svc: "Frame Powder Coat · Gloss" },
        { id: "mc9",  day: "fri", time: "8am",  crew: "A", addr: "'55 Indian Chief Blackhawk · SN CS-341102",     svc: "Leather Saddle Re-Stitch" },
        { id: "mc10", day: "fri", time: "2pm",  crew: "B", addr: "'74 Moto Guzzi 850 T · SN 850T-VE-3311",        svc: "Patina Preservation · Clear" },
      ],
    },
    spine: {
      kicker: "Module 03 · Utility Spine",
      title: "Every machine, every quirk, remembered.",
      blurb:
        "Search 'kick-only' or 'matching numbers' across every bench card on file. Restorers add notes voice-to-text from the bench. Nothing gets forgotten between visits.",
      addAction: "Add Owner Note",
      newFileLabel: "New Owner File",
      entityLabel: "Owner Name",
      addrLabel: "Machine · Serial",
      counterLabel: "Owner files",
      files: [
        { id: "o1", name: "Whitaker, Dell",   addr: "'68 Triumph Bonneville · SN T120R-DU-84921", last: "Nov 04 · Tank Strip",             next: "Feb 03 · Tank Re-Paint + Pinstripe", note: "Wants factory Kelly Green — NOT metallic. Matching numbers frame — DO NOT re-stamp anything. Owner is a machinist, will notice." },
        { id: "o2", name: "Alderson, Mira",   addr: "'72 Norton Commando 750 · SN 220134",         last: "Sep 18 · Carb Teardown",         next: "Feb 03 · Amal Rebuild",              note: "Bike is a daily-rider — needs to fire on the third kick every time. Idle high, she likes it lumpy." },
        { id: "o3", name: "Petrescu, Ilie",   addr: "'65 BSA Lightning Rocket · SN A65LR-2841",    last: "Oct 22 · Chrome Prep",           next: "Feb 04 · Full Chrome Re-Plate",       note: "Show bike — going to Barber Vintage Fest April 26. Chrome must be pit-free. Send photos before install." },
        { id: "o4", name: "Bosch, Reinhardt", addr: "'59 Harley Panhead · SN 59FL-1104",           last: "Jul 07 · Cases Split",           next: "Feb 05 · Bottom-End Rebuild",         note: "Numbers-matching Pan. Original tin. Do not sandblast anything — hand-strip only. Owner tips cash for careful work." },
        { id: "o5", name: "Kerrigan, Vale",   addr: "'74 Moto Guzzi 850 T · SN 850T-VE-3311",      last: "Jan 15 · Patina Assessment",     next: "Feb 05 · Patina Clear Coat",         note: "PRESERVE the patina — no polishing, no touch-up paint. Owner wants the 50-year story left on the tank. Clear-coat only." },
      ],
    },
    billing: {
      kicker: "Module 04 · Billing Autopilot",
      title: "Machine done → Invoice sent. One button.",
      blurb:
        "The moment the machine rolls off the bench, tap Send Invoice. Amanda sends the bill, texts the owner a pay-link, and files the restoration in the Grid.",
      entityLabel: "Owner",
      openLabel: "Bench cards waiting",
      invoicedLabel: "Invoiced today",
      billedLabel: "Billed today",
      invoices: [
        { id: "inv1", addr: "'68 Triumph Bonneville · Whitaker",   svc: "Tank Re-Paint + Pinstripe",   crew: "Bench 1", date: "Feb 03", price: 1240 },
        { id: "inv2", addr: "'72 Norton Commando · Alderson",      svc: "Carb Rebuild · Pair Amal",    crew: "Bench 2", date: "Feb 03", price: 680  },
        { id: "inv3", addr: "'65 BSA Lightning · Petrescu",        svc: "Full Chrome Re-Plate",        crew: "Bench 1", date: "Feb 04", price: 2450 },
        { id: "inv4", addr: "'59 Harley Panhead · Bosch",          svc: "Bottom-End Rebuild",          crew: "Bench 2", date: "Feb 04", price: 4880 },
        { id: "inv5", addr: "'74 Moto Guzzi 850 T · Kerrigan",     svc: "Patina Preservation · Clear", crew: "Bench 1", date: "Feb 05", price: 890  },
      ],
    },
    growth: {
      kicker: "Module 05 · Growth Engine",
      title: "Ten videos a week — Amanda drafts them all.",
      blurb:
        "Amanda tracks the vintage scene for high-engagement themes for your weekly shoots. Patina reveals, tank strips, chrome polish reels, and owner reunions — auto-drafted, scheduled, and posted.",
      pill: "10 High-Quality Strategic Assets/Week",
      videos: [
        { day: "Mon", slot: "AM", type: "Patina Reveal",   title: "50-Year Guzzi Tank · Clear-Coat Save" },
        { day: "Mon", slot: "PM", type: "Seasonal Tip",    title: "Winter Storage · Vintage Fuel Systems" },
        { day: "Tue", slot: "AM", type: "Bench Spotlight", title: "Meet Cal · Lead Restorer on Bonnevilles" },
        { day: "Wed", slot: "AM", type: "Owner Story",     title: "Whitaker · Grandfather's '68 Bonneville" },
        { day: "Wed", slot: "PM", type: "Strip Reel",      title: "Full Tank Strip · '76 CB750" },
        { day: "Thu", slot: "AM", type: "Quick Tip",       title: "60-Second Chrome Polish Trick" },
        { day: "Fri", slot: "AM", type: "Chrome Reel",     title: "Chrome Re-Plate · '65 BSA Lightning" },
        { day: "Fri", slot: "PM", type: "Bench Reel",      title: "This Week on the Benches" },
        { day: "Sat", slot: "AM", type: "Show Push",       title: "Barber Vintage Fest · 6 Machines Going" },
        { day: "Sun", slot: "PM", type: "Testimonial",     title: "Owner of the Month · Bosch Panhead" },
      ],
    },
  },

  // ---------------------------------------------------------
  // MIXOLOGY · The Vault Room — craft mixology / cocktail bar.
  // Lead Architect: John Delawder. Phone: 440-771-8409.
  // Vocabulary MUST stay isolated from every other vertical:
  // no "yard", no "band", no "bay", no "load", no "bench" here.
  // Mixology-only nouns: bar, rail, reservation, VIP, table,
  // recipe, pour, spec, cocktail, garnish, vendor, distributor,
  // party, tasting.
  // ---------------------------------------------------------
  MIXOLOGY: {
    key: "MIXOLOGY",
    brand: "The Vault Room",
    location: "Cleveland, OH",
    trade: "Craft Mixology",
    leadArchitect: "John Delawder",
    hero: {
      kicker: "The Vault Room · Founder Greeting",
      subject: "bar business",
    },
    brandStripe: {
      title:
        "A craft cocktail room running on the Grid — every reservation captured, every recipe filed,",
      titleTail: "every top-shelf pour remembered.",
    },
    intakeShield: {
      kicker: "Module 01 · Intake Shield",
      title: "Every reservation captured. Every VIP request confirmed.",
      blurb:
        "Amanda answers every inbound call to 440-771-8409 — books the table, logs the VIP request, or schedules a callback if the bar is slammed on a Friday.",
      booking: {
        kicker: "Book a Table",
        title: "The Reservations Desk",
        blurb:
          "Tap any open slot to reserve that seating window. Amanda locks it in and texts a confirmation card the second you send.",
        slotAction: "Reserve This Table",
        crewALabel: "Front Bar",
        crewBLabel: "VIP Room",
      },
      callMeLater: {
        kicker: "No-Miss Guarantee",
        title: "Call Me Later",
        idleCopy:
          "Guest can't talk over the crowd? Tap the button. Amanda picks the moment, sends a text, and calls back on their window — not the bar's.",
        contactLabel: "Guest Phone (Amanda texts the reservation card)",
        brandLine: "The Vault Room · Amanda:",
        callFrom: "(440) 771-8409",
      },
      proof: {
        title: "Last Night · Amanda's Watch",
        stats: [
          { k: "Reservation calls", v: "58" },
          { k: "Tables confirmed",  v: "34" },
          { k: "Walk-off guests",   v: "0"  },
        ],
      },
    },
    commandCalendar: {
      kicker: "Module 02 · Command Calendar",
      title: "Live floor board — every shift, every event, every night.",
      blurb:
        "Amanda plots the schedule overnight. Bartenders walk in at 4pm and every seat, every private event, every staff assignment is already staged on the floor plan.",
      addAction: "Schedule Shift",
      newLabel: "New Floor Card",
      addrLabel: "Event · Shift",
      addrPlaceholder: "Private Party · Suite 2 · 30 guests",
      svcLabel: "Assignment",
      svcPlaceholder: "Nate on service · Priya on bar back",
      footerCount: (n) => `${n} floor cards on the board`,
      jobs: [
        { id: "v1",  day: "mon", time: "4pm",  crew: "A", addr: "Whiskey Flight Tasting · 12 seats",     svc: "Nate on service · Cal on pours" },
        { id: "v2",  day: "mon", time: "8am",  crew: "B", addr: "Distributor Delivery · Buffalo Trace",  svc: "Priya receives · dock 2" },
        { id: "v3",  day: "tue", time: "10am", crew: "A", addr: "Staff Training · House Specs",          svc: "Delawder leads · new hire onboard" },
        { id: "v4",  day: "tue", time: "2pm",  crew: "B", addr: "Sommelier Session · Wine Pairing",      svc: "Riley leads · 8 seats confirmed" },
        { id: "v5",  day: "wed", time: "8am",  crew: "A", addr: "Corporate Dinner · Progressive Ins.",   svc: "VIP Room · 24 guests · plated" },
        { id: "v6",  day: "wed", time: "12pm", crew: "B", addr: "Bartender Interview · Front Bar",       svc: "Delawder + Nate · trial pour" },
        { id: "v7",  day: "thu", time: "10am", crew: "A", addr: "Bourbon Society Meeting · Suite 1",     svc: "16 seats · rare pours pre-set" },
        { id: "v8",  day: "thu", time: "4pm",  crew: "B", addr: "DJ Residency · Vega · Front Bar",       svc: "Extended set · 10p to 2a" },
        { id: "v9",  day: "fri", time: "8am",  crew: "A", addr: "Menu Photography · New Cocktails",      svc: "Chef Diaz · 8 signatures shot" },
        { id: "v10", day: "fri", time: "2pm",  crew: "B", addr: "Birthday Buyout · The Vault Room",      svc: "45 guests · open bar · 6pm to 11pm" },
      ],
    },
    spine: {
      kicker: "Module 03 · Utility Spine · The Vault",
      title: "Every recipe, every bottle, remembered.",
      blurb:
        "Search 'house Old Fashioned' or 'Yamazaki 12' across every recipe and inventory line on file. Bartenders add spec notes voice-to-text from the well. Nothing gets forgotten between shifts.",
      addAction: "Add Recipe · Bottle",
      newFileLabel: "New Vault Entry",
      entityLabel: "Recipe / Bottle Name",
      addrLabel: "Category · Shelf",
      counterLabel: "Vault entries",
      files: [
        { id: "vr1", name: "House Old Fashioned",     addr: "Signature · Well Rail",           last: "Poured 42x last night",         next: "Restock Peychaud's Thu",         note: "2 oz Buffalo Trace, .25 oz Demerara syrup, 3 dashes Angostura, 1 dash Peychaud's. Orange peel expressed, no cherry. Delawder spec — do not deviate." },
        { id: "vr2", name: "Smoked Manhattan",        addr: "Signature · Top Shelf",           last: "Poured 18x last night",         next: "Cherrywood chip re-order",       note: "Cherrywood smoke under coupe glass, 60 sec. 2 oz Michter's rye, 1 oz Antica Formula, 2 dash Angostura. Brandied cherry ONLY — no maraschino." },
        { id: "vr3", name: "Yamazaki 12 · Bottle",    addr: "Inventory · Locked Cabinet 3",     last: "8 bottles on hand",             next: "Reorder trigger at 3 bottles",   note: "$185 per pour retail. Bottle only leaves cabinet with Delawder's sign-off. Photograph every open bottle for the vault log." },
        { id: "vr4", name: "Espresso Martini · House",addr: "Signature · Well Rail",           last: "Poured 27x last night",         next: "Fresh beans Wed",                note: "Diplomático Reserva Exclusiva, NOT vodka. Freshly pulled shot from Diaz's La Marzocco. Three coffee beans on the foam — always three, never two." },
        { id: "vr5", name: "Pappy Van Winkle 15",     addr: "Inventory · Vault Safe",          last: "2 bottles on hand · off menu",  next: "Bourbon Society allocation Mar", note: "$225 per pour. Members only. Requires Delawder physical release. Log the pour and the guest name in the Vault ledger before serving." },
      ],
    },
    billing: {
      kicker: "Module 04 · Billing Autopilot · Quick-Check",
      title: "Event closed → Quick-Check sent. One button.",
      blurb:
        "The moment the private event wraps or the vendor invoice lands, tap Send Quick-Check. Amanda mails the bill, texts the host a pay-link, and files the tab in the Vault.",
      entityLabel: "Host · Vendor",
      openLabel: "Quick-Checks waiting",
      invoicedLabel: "Sent today",
      billedLabel: "Collected today",
      invoices: [
        { id: "inv1", addr: "Progressive Ins. Corporate Dinner",  svc: "VIP Room · 24 guests · plated + open bar", crew: "VIP Room",  date: "Feb 03", price: 3800 },
        { id: "inv2", addr: "Buffalo Trace · Feb Distributor Bill", svc: "Case · 12 x Antique Collection",         crew: "Vendor",    date: "Feb 03", price: 4200 },
        { id: "inv3", addr: "Whiskey Flight Tasting · Bourbon Soc.", svc: "12 seats · 4-pour flight + snacks",     crew: "Front Bar", date: "Feb 04", price: 960  },
        { id: "inv4", addr: "Suite 1 · Birthday Buyout",             svc: "45 guests · 5-hour open bar",           crew: "VIP Room",  date: "Feb 04", price: 5400 },
        { id: "inv5", addr: "Glassware · Riedel · Reorder",          svc: "Vendor · 48 coupes + 24 rocks",         crew: "Vendor",    date: "Feb 05", price: 720  },
      ],
    },
    growth: {
      kicker: "Module 05 · Growth Engine · Visual Menu",
      title: "Ten cinematic cocktail cuts a week — Amanda drafts them all.",
      blurb:
        "Amanda tracks the local nightlife for high-engagement themes for your weekly shoots. Pour reveals, smoke shots, garnish plating, bartender features, and rare-bottle unlocks — auto-drafted, scheduled, and posted.",
      pill: "10 High-End Cinematic Cuts/Week",
      videos: [
        { day: "Mon", slot: "AM", type: "Pour Reveal",       title: "The House Old Fashioned · Slow-Motion Pour" },
        { day: "Mon", slot: "PM", type: "Seasonal Feature",  title: "Winter Menu Drop · 4 New Signatures" },
        { day: "Tue", slot: "AM", type: "Bartender Feature", title: "Meet Nate · Head Bartender · Rail to VIP" },
        { day: "Wed", slot: "AM", type: "Guest Story",       title: "The Bourbon Society · 5 Years at the Vault" },
        { day: "Wed", slot: "PM", type: "Smoke Reveal",      title: "Smoked Manhattan Under the Coupe" },
        { day: "Thu", slot: "AM", type: "Quick Tip",         title: "60-Second Expressed Orange Peel Technique" },
        { day: "Fri", slot: "AM", type: "Rare Bottle Reel",  title: "Yamazaki 12 · Pour of the Week" },
        { day: "Fri", slot: "PM", type: "Room Reel",         title: "Friday Night at The Vault Room" },
        { day: "Sat", slot: "AM", type: "Reservation Push",  title: "Valentine's Buyout · 3 Suites Left" },
        { day: "Sun", slot: "PM", type: "Guest of the Night",title: "Regular of the Week · The Corner Two-Top" },
      ],
    },
  },

  // ---------------------------------------------------------
  // MAGIC_GARAGE · Magic Garage Door (Mansfield, OH)
  // Lead Architect: Sabrina Wyeth. Phone: 419-529-2002.
  // Vocabulary MUST stay isolated: no "yard", no "band", no
  // "bay" (that's MOTO), no "load", no "bench", no "reservation".
  // Garage-only nouns: door, opener, spring, cable, panel,
  // track, install, quote, property, on-site.
  // ---------------------------------------------------------
  MAGIC_GARAGE: {
    key: "MAGIC_GARAGE",
    brand: "Magic Garage Door",
    location: "Mansfield, OH",
    trade: "Garage Door Service",
    leadArchitect: "Sabrina Wyeth",
    hero: {
      kicker: "Magic Garage Door · Founder Greeting",
      subject: "door business",
    },
    brandStripe: {
      title:
        "A garage door operation running on the Grid — every emergency captured, every quote filed,",
      titleTail: "every spring cycle remembered.",
    },
    intakeShield: {
      kicker: "Module 01 · Intake Shield",
      title: "Every emergency captured. Every quote request confirmed.",
      blurb:
        "Amanda answers every inbound call to 419-529-2002 — books the on-site quote, logs the emergency ticket, or schedules a callback if the crew is up on a ladder.",
      booking: {
        kicker: "Book a Service Call",
        title: "The Dispatch Board",
        blurb:
          "Tap any open slot to lock that service window. Amanda locks it in and texts a dispatch ticket the second you send.",
        slotAction: "Dispatch This Slot",
        crewALabel: "Install Crew",
        crewBLabel: "Repair Crew",
      },
      callMeLater: {
        kicker: "No-Miss Guarantee",
        title: "Call Me Later",
        idleCopy:
          "Property owner can't talk while the door is stuck? Tap the button. Amanda picks the moment, sends a text, and calls back on their window — not yours.",
        contactLabel: "Property Owner Phone (Amanda texts the dispatch ticket)",
        brandLine: "Magic Garage Door · Amanda:",
        callFrom: "(419) 529-2002",
      },
      proof: {
        title: "Yesterday · Amanda's Watch",
        stats: [
          { k: "Emergency calls",  v: "22" },
          { k: "Quotes booked",    v: "16" },
          { k: "Lost tickets",     v: "0"  },
        ],
      },
    },
    commandCalendar: {
      kicker: "Module 02 · Command Calendar",
      title: "Live dispatch board — every install, every repair, every day.",
      blurb:
        "Amanda plots the runs overnight. Techs pull the van at 7am and every stop is already staged — property address, door model, and quote notes on the ticket.",
      addAction: "Dispatch Team",
      newLabel: "New Dispatch Ticket",
      addrLabel: "Property · Door Model",
      addrPlaceholder: "247 Oak Grove Dr · Overhead 16x7 · 8000 series",
      svcLabel: "Service Type",
      svcPlaceholder: "Torsion Spring Replacement",
      footerCount: (n) => `${n} tickets on the board`,
      jobs: [
        { id: "g1",  day: "mon", time: "8am",  crew: "A", addr: "247 Oak Grove Dr · Overhead 16x7",         svc: "Full Sectional Install · Steel" },
        { id: "g2",  day: "mon", time: "12pm", crew: "B", addr: "3418 Trimble Rd · Wayne-Dalton 9100",       svc: "Torsion Spring Replacement · Dual" },
        { id: "g3",  day: "tue", time: "10am", crew: "A", addr: "812 Ashland Ave · Amarr Classica 3138",     svc: "Carriage-Style Door Install" },
        { id: "g4",  day: "tue", time: "2pm",  crew: "B", addr: "1690 Lexington-Springmill · CHI 5216",      svc: "Cable + Roller Emergency Repair" },
        { id: "g5",  day: "wed", time: "8am",  crew: "A", addr: "55 Park Ave West · Clopay Gallery 3720",    svc: "LiftMaster 8500W Wall-Mount Opener" },
        { id: "g6",  day: "wed", time: "12pm", crew: "B", addr: "2201 Lexington Ave · Overhead 9700",        svc: "Section Panel Replacement · Bottom" },
        { id: "g7",  day: "thu", time: "10am", crew: "A", addr: "4400 Marion Ave · Wayne-Dalton 8500",       svc: "Track Alignment + Balance Test" },
        { id: "g8",  day: "thu", time: "4pm",  crew: "B", addr: "133 W 4th St · Amarr Heritage",             svc: "Emergency After-Hours Cable Snap" },
        { id: "g9",  day: "fri", time: "8am",  crew: "A", addr: "710 N Diamond St · Clopay Coachman 4050",   svc: "Insulated Door Upgrade · 3-Car" },
        { id: "g10", day: "fri", time: "2pm",  crew: "B", addr: "2800 Springmill Rd · CHI 2216",             svc: "Winter Weather-Seal Retrofit" },
      ],
    },
    spine: {
      kicker: "Module 03 · Utility Spine · Door Vault",
      title: "Every door, every spring cycle, remembered.",
      blurb:
        "Search '16x7 torsion' or 'LiftMaster 8500W chip' across every property on file. Techs add spec notes voice-to-text from the driveway. Nothing gets forgotten between service calls.",
      addAction: "Add Door Spec",
      newFileLabel: "New Property File",
      entityLabel: "Property Owner",
      addrLabel: "Property · Door Model",
      counterLabel: "Property files",
      files: [
        { id: "d1", name: "Ellison, Marcus",   addr: "247 Oak Grove Dr · Overhead 16x7 8000",    last: "Nov 04 · Balance Tune-Up",       next: "Feb 03 · Full Sectional Install",  note: "Existing torsion is 20,000-cycle — swap to 25,000-cycle for the new door. Owner wants belt drive, NOT chain. Detached garage — no attic pull-down." },
        { id: "d2", name: "Kowalski, Jenna",   addr: "3418 Trimble Rd · Wayne-Dalton 9100",       last: "Aug 18 · Cable Replacement",     next: "Feb 03 · Torsion Spring Replacement", note: "Dual spring setup — both broke within a week. Property owner asked for wound-up cycle count on the paperwork. Do not leave without balance test signed off." },
        { id: "d3", name: "Ochoa, Ren",        addr: "812 Ashland Ave · Amarr Classica 3138",     last: "Oct 22 · Estimate Walk",         next: "Feb 04 · Carriage-Style Install",     note: "Historic neighborhood — carriage-style is required by HOA. Doors must ship painted Charleston Green from factory. 6-8 week lead time already banked." },
        { id: "d4", name: "Petronis, Mia",     addr: "1690 Lexington-Springmill · CHI 5216",      last: "Jan 12 · Emergency Cable",       next: "Feb 04 · Cable + Roller Repair",     note: "Second cable snap in six months — recommend upgrading rollers to nylon and inspecting track deflection. Owner works nights, only reachable 10a-2p." },
        { id: "d5", name: "Whitehead, Dre",    addr: "710 N Diamond St · Clopay Coachman 4050",   last: "Sep 15 · Weather-Seal Bid",      next: "Feb 05 · Insulated 3-Car Upgrade",    note: "R-value 18 insulated panels — owner drives an EV, garage doubles as workshop in winter. Program 3 keypads and 4 remotes. Sync with existing myQ hub." },
      ],
    },
    billing: {
      kicker: "Module 04 · Billing Autopilot",
      title: "Install done → Invoice sent. One button.",
      blurb:
        "The moment the truck rolls off the property, tap Send Invoice. Amanda sends the bill, texts the property owner a pay-link, and files the ticket in the Grid.",
      entityLabel: "Property Owner",
      openLabel: "Tickets waiting",
      invoicedLabel: "Invoiced today",
      billedLabel: "Billed today",
      invoices: [
        { id: "inv1", addr: "247 Oak Grove Dr · Ellison",         svc: "Full Sectional Install · Steel 16x7",     crew: "Install Crew", date: "Feb 03", price: 2480 },
        { id: "inv2", addr: "3418 Trimble Rd · Kowalski",         svc: "Torsion Spring Replacement · Dual",       crew: "Repair Crew",  date: "Feb 03", price: 640  },
        { id: "inv3", addr: "812 Ashland Ave · Ochoa",            svc: "Carriage-Style Door Install · Amarr",     crew: "Install Crew", date: "Feb 04", price: 3890 },
        { id: "inv4", addr: "1690 Lexington-Springmill · Petronis", svc: "Cable + Roller Emergency Repair",       crew: "Repair Crew",  date: "Feb 04", price: 420  },
        { id: "inv5", addr: "710 N Diamond St · Whitehead",       svc: "Insulated 3-Car Door Upgrade + Openers",  crew: "Install Crew", date: "Feb 05", price: 5240 },
      ],
    },
    growth: {
      kicker: "Module 05 · Growth Engine · Visual Safety",
      title: "Ten safety cuts a week — Amanda drafts them all.",
      blurb:
        "Amanda tracks the local property scene for high-engagement themes. Safety demos, before/after installs, spring inspections, and property owner reveals — auto-drafted, scheduled, and posted.",
      pill: "10 High-Impact Safety Cuts/Week",
      videos: [
        { day: "Mon", slot: "AM", type: "Safety Demo",     title: "60-Second Torsion Spring Danger Check" },
        { day: "Mon", slot: "PM", type: "Seasonal Push",   title: "Winter Weather-Seal Retrofits · 3 Slots Left" },
        { day: "Tue", slot: "AM", type: "Tech Feature",    title: "Meet Reggie · Head Install Tech · 12 Years" },
        { day: "Wed", slot: "AM", type: "Before/After",    title: "Rotted Wood Door → Fresh Carriage-Style" },
        { day: "Wed", slot: "PM", type: "Emergency Reel",  title: "After-Hours Cable Snap · Same-Night Fix" },
        { day: "Thu", slot: "AM", type: "Quick Tip",       title: "How to Test Your Door's Balance in 30 Seconds" },
        { day: "Fri", slot: "AM", type: "Install Reel",    title: "Full Sectional Install · Oak Grove Dr" },
        { day: "Fri", slot: "PM", type: "Team Reel",       title: "This Week on the Trucks" },
        { day: "Sat", slot: "AM", type: "Safety Warning",  title: "Never Fix a Torsion Spring Yourself · Here's Why" },
        { day: "Sun", slot: "PM", type: "Testimonial",     title: "Property Owner of the Month · Ashland Ave" },
      ],
    },
  },

  // ---------------------------------------------------------
  // MSQUARED · M Squared LLC (Marion, OH)
  // Lead Architect: Bob Wyeth. Phone: 740-514-4652.
  // Vocabulary MUST stay isolated: no "yard", no "backstage",
  // no "bay", no "load", no "bench", no "reservation", no "door".
  // Masonry-only nouns: bid, GC, project, site, foundation,
  // slab, block, brick, stone, mortar, pour, elevation, milestone,
  // blueprint, draw.
  // ---------------------------------------------------------
  MSQUARED: {
    key: "MSQUARED",
    brand: "M Squared LLC",
    location: "Marion, OH",
    trade: "Masonry & Stone",
    leadArchitect: "Bob Wyeth",
    hero: {
      kicker: "M Squared LLC · Founder Greeting",
      subject: "masonry business",
    },
    brandStripe: {
      title:
        "A masonry crew running on the Grid — every bid captured, every blueprint filed,",
      titleTail: "every milestone remembered.",
    },
    intakeShield: {
      kicker: "Module 01 · Intake Shield",
      title: "Every bid captured. Every consult confirmed.",
      blurb:
        "Amanda answers every inbound call to 740-514-4652 — books the site walk, logs the GC bid request, or schedules a callback if the crew is trowel-deep in mortar.",
      booking: {
        kicker: "Book a Bid Walk",
        title: "The Consult Calendar",
        blurb:
          "Tap any open slot to lock that site window. Amanda locks it in and texts a consult card the second you send.",
        slotAction: "Book This Walk",
        crewALabel: "Bid Team",
        crewBLabel: "Build Team",
      },
      callMeLater: {
        kicker: "No-Miss Guarantee",
        title: "Call Me Later",
        idleCopy:
          "GC can't talk from the trailer? Tap the button. Amanda picks the moment, sends a text, and calls back on their window — not the crew's.",
        contactLabel: "GC · Project Owner Phone (Amanda texts the consult card)",
        brandLine: "M Squared LLC · Amanda:",
        callFrom: "(740) 514-4652",
      },
      proof: {
        title: "Yesterday · Amanda's Watch",
        stats: [
          { k: "Bid calls",     v: "14" },
          { k: "Walks booked",  v: "9"  },
          { k: "Lost bids",     v: "0"  },
        ],
      },
    },
    commandCalendar: {
      kicker: "Module 02 · Command Calendar",
      title: "Live project board — every site, every milestone, every day.",
      blurb:
        "Amanda plots the schedule overnight. Foremen roll trucks at 6am and every site is already staged — elevation prints, mix specs, and GC contacts on the milestone card.",
      addAction: "Schedule Milestone",
      newLabel: "New Milestone Card",
      addrLabel: "Site · Elevation",
      addrPlaceholder: "Marion High School · East Elevation",
      svcLabel: "Milestone Type",
      svcPlaceholder: "Foundation Pour · 40 CY · 4000psi",
      footerCount: (n) => `${n} milestones on the board`,
      jobs: [
        { id: "m1",  day: "mon", time: "8am",  crew: "A", addr: "Marion Elementary · East Wing Foundation",    svc: "Slab Pour · 40 CY · 4000psi" },
        { id: "m2",  day: "mon", time: "12pm", crew: "B", addr: "Bucyrus Rd Retaining Wall · Phase 2",         svc: "Fieldstone Course · 42 lin ft" },
        { id: "m3",  day: "tue", time: "10am", crew: "A", addr: "Ridgemont Chimney · Rebuild · Wyeth spec",    svc: "Brick Corbel · Course 1-8" },
        { id: "m4",  day: "tue", time: "2pm",  crew: "B", addr: "Prospect St Firehouse · CMU Bay Wall",        svc: "Block Wall · CMU 8 · 220 sf" },
        { id: "m5",  day: "wed", time: "8am",  crew: "A", addr: "Delaware County Courthouse · Facade",         svc: "Limestone Veneer · Section C" },
        { id: "m6",  day: "wed", time: "12pm", crew: "B", addr: "Marion Twp Fire Station · Slab",              svc: "Slab-on-Grade Prep + Rebar Tie-In" },
        { id: "m7",  day: "thu", time: "10am", crew: "A", addr: "Sawmill Village · Amenity Center Chimney",    svc: "Full Rebuild · Roof-Line Up" },
        { id: "m8",  day: "thu", time: "4pm",  crew: "B", addr: "Prospect Historic Row · Restoration",         svc: "Tuckpoint + Repoint · 3 units" },
        { id: "m9",  day: "fri", time: "8am",  crew: "A", addr: "M Squared HQ · Yard Sample Mockup",           svc: "Wyeth Pattern Sample Wall" },
        { id: "m10", day: "fri", time: "2pm",  crew: "B", addr: "Buckeye Central Athletics · Bleacher Base",   svc: "Concrete Bleacher Slab Pour" },
      ],
    },
    spine: {
      kicker: "Module 03 · Utility Spine · Blueprint Vault",
      title: "Every blueprint, every mix, remembered.",
      blurb:
        "Search 'Wyeth pattern' or 'type-S mortar' across every project on file. Foremen add material notes voice-to-text from the trailer. Nothing gets forgotten between draws.",
      addAction: "Add Blueprint · Mix",
      newFileLabel: "New Project File",
      entityLabel: "GC · Project Owner",
      addrLabel: "Site · Blueprint ID",
      counterLabel: "Project files",
      files: [
        { id: "p1", name: "Ohler Construction · Marion Schools", addr: "Marion Elementary · BP-2024-E-11",       last: "Jan 24 · Excavation Walk",       next: "Feb 03 · Slab Pour Draw",         note: "4000psi mix per structural. Rebar chair'd 3\" up per architect note. GC Ken Ohler wants photos of every rebar tie before pour. NO exceptions." },
        { id: "p2", name: "County Roads · Bucyrus",              addr: "Bucyrus Rd Wall · BP-2024-BR-04",         last: "Dec 12 · Phase 1 Course",        next: "Feb 03 · Phase 2 Fieldstone",     note: "Sourced fieldstone from Ridgemont Quarry — 2 pallets pre-staged. County inspector Jerry needs to see joint width before backfill. Type-S mortar only." },
        { id: "p3", name: "Ridgemont HOA · Wyeth Chimney",       addr: "Ridgemont Ln · BP-Wyeth-CH-01",           last: "Nov 04 · Demo",                  next: "Feb 04 · Corbel Course 1-8",      note: "Family requested Bob's signature Wyeth corbel pattern — sample wall is at HQ. Do not deviate. Historic brick was salvaged and cleaned — use throughout." },
        { id: "p4", name: "Delaware County · Courthouse Facade", addr: "Delaware Courthouse · BP-DC-FCE-22",      last: "Jan 15 · Section B",             next: "Feb 05 · Section C Veneer",       note: "Indiana Limestone Institute-certified stone. Course lines MUST match Section B within 1/8\". Court is in session — no impact tools between 9am and 4pm." },
        { id: "p5", name: "Sawmill Village LLC · Amenity",       addr: "Sawmill Village · BP-SV-AC-07",           last: "Dec 30 · Assessment",            next: "Feb 06 · Full Rebuild",           note: "Original chimney rebuilt from footing up. Owner wants copper flashing at cap. New rebar cage inside, matching the 1928 elevation drawing on file." },
      ],
    },
    billing: {
      kicker: "Module 04 · Billing Autopilot · Milestone Draws",
      title: "Milestone hit → Draw invoice sent. One button.",
      blurb:
        "The moment the milestone lands, tap Send Draw. Amanda mails the draw invoice with photo proof, texts the GC a pay-link, and files the draw in the Grid.",
      entityLabel: "GC · Project Owner",
      openLabel: "Draws waiting",
      invoicedLabel: "Draws sent today",
      billedLabel: "Collected today",
      invoices: [
        { id: "inv1", addr: "Marion Elementary · Ohler Construction", svc: "Draw 2 · Foundation Slab Complete",     crew: "Build Team", date: "Feb 03", price: 18400 },
        { id: "inv2", addr: "Bucyrus Rd Wall · County Roads",         svc: "Draw 3 · Phase 2 Fieldstone Course",   crew: "Build Team", date: "Feb 03", price: 8600  },
        { id: "inv3", addr: "Ridgemont Chimney · HOA",                svc: "Draw 1 · Demo + Corbel Base Course",   crew: "Build Team", date: "Feb 04", price: 6200  },
        { id: "inv4", addr: "Delaware Courthouse · Delaware County",  svc: "Draw 4 · Section C Limestone Veneer",  crew: "Build Team", date: "Feb 04", price: 24800 },
        { id: "inv5", addr: "Sawmill Amenity · Sawmill Village LLC",  svc: "Draw 1 · Assessment + Footing Prep",   crew: "Build Team", date: "Feb 05", price: 4900  },
      ],
    },
    growth: {
      kicker: "Module 05 · Growth Engine · Site Pulse",
      title: "Ten site cuts a week — Amanda drafts them all.",
      blurb:
        "Amanda tracks the local construction scene for high-engagement themes. Structural walk-throughs, foundation pours, chimney rebuilds, and GC testimonials — auto-drafted, scheduled, and posted.",
      pill: "10 High-Impact Site Cuts/Week",
      videos: [
        { day: "Mon", slot: "AM", type: "Pour Reel",         title: "40 CY Slab Pour · Marion Elementary · Timelapse" },
        { day: "Mon", slot: "PM", type: "Structural Tip",    title: "Why Type-S Mortar for Load-Bearing Walls" },
        { day: "Tue", slot: "AM", type: "Foreman Feature",   title: "Meet Del · Lead Foreman on Fieldstone Runs" },
        { day: "Wed", slot: "AM", type: "GC Story",          title: "Ohler Construction · 12 Years Building With Us" },
        { day: "Wed", slot: "PM", type: "Rebuild Reel",      title: "Ridgemont Chimney · Corbel Course by Course" },
        { day: "Thu", slot: "AM", type: "Quick Tip",         title: "60-Second Joint-Width Inspection Trick" },
        { day: "Fri", slot: "AM", type: "Veneer Reel",       title: "Limestone Facade · Delaware Courthouse" },
        { day: "Fri", slot: "PM", type: "Crew Reel",         title: "This Week on the Sites" },
        { day: "Sat", slot: "AM", type: "Bid Push",          title: "Spring Build Slots · 4 Foundations Open" },
        { day: "Sun", slot: "PM", type: "Testimonial",       title: "GC of the Month · Delaware County" },
      ],
    },
  },

  // ---------------------------------------------------------
  // AFFORDABLE · Affordable Outdoor Property Management (Ohio)
  // Lead Architect: Elvis. Phone: 419-529-2002.
  // Vocabulary MUST stay isolated: no "yard" (that's LAWN),
  // no "band", no "bay", no "load", no "bench", no "door",
  // no "site" (that's MSQUARED). Property-management-only nouns:
  // property, portfolio, unit, tenant, owner, work order,
  // maintenance ticket, walkthrough, inspection, service call,
  // recurring plan, seasonal turn.
  // ---------------------------------------------------------
  AFFORDABLE: {
    key: "AFFORDABLE",
    brand: "Affordable Outdoor Property Management",
    location: "Ohio · Statewide Portfolios",
    trade: "Outdoor Property Management",
    leadArchitect: "Elvis",
    hero: {
      kicker: "Affordable Outdoor · Founder Greeting",
      subject: "property management business",
    },
    brandStripe: {
      title:
        "A property management operation running on the Grid — every inquiry captured, every work order filed,",
      titleTail: "every property remembered.",
    },
    intakeShield: {
      kicker: "Module 01 · Intake Shield",
      title: "Every inquiry captured. Every maintenance request confirmed.",
      blurb:
        "Amanda answers every inbound call to 419-529-2002 — books the property walkthrough, logs the maintenance ticket, or schedules a callback if the crew is on a mower or under a hedge.",
      booking: {
        kicker: "Book a Service Call",
        title: "The Dispatch Board",
        blurb:
          "Tap any open slot to lock that service window. Amanda locks it in and texts a work order the second you send.",
        slotAction: "Dispatch This Slot",
        crewALabel: "Mow Crew",
        crewBLabel: "Maintenance Crew",
      },
      callMeLater: {
        kicker: "No-Miss Guarantee",
        title: "Call Me Later",
        idleCopy:
          "Property owner can't talk right now? Tap the button. Amanda picks the moment, sends a text, and calls back on their window — not the crew's.",
        contactLabel: "Property Owner Phone (Amanda texts the work order)",
        brandLine: "Affordable Outdoor · Amanda:",
        callFrom: "(419) 529-2002",
      },
      proof: {
        title: "Yesterday · Amanda's Watch",
        stats: [
          { k: "Inquiries captured", v: "24" },
          { k: "Work orders booked", v: "17" },
          { k: "Lost tickets",       v: "0"  },
        ],
      },
    },
    commandCalendar: {
      kicker: "Module 02 · Command Calendar",
      title: "Live dispatch board — every property, every crew, every day.",
      blurb:
        "Amanda plots the runs overnight. Crews open the app at 6:30am and every property is already loaded — address, service scope, and gate/access notes on the work order.",
      addAction: "Schedule Work Order",
      newLabel: "New Work Order",
      addrLabel: "Property Address",
      addrPlaceholder: "3120 Trimble Rd · Mansfield, OH · Duplex",
      svcLabel: "Service Type",
      svcPlaceholder: "Weekly Mow + Trim Hedges",
      footerCount: (n) => `${n} work orders on the board`,
      jobs: [
        { id: "a1",  day: "mon", time: "8am",  crew: "A", addr: "3120 Trimble Rd · Mansfield · Duplex",           svc: "Weekly Mow + Edge · 0.4 acre" },
        { id: "a2",  day: "mon", time: "12pm", crew: "B", addr: "812 N Main St · Mansfield · Fourplex Portfolio", svc: "Hedge Trim + Bed Weed · Common Area" },
        { id: "a3",  day: "tue", time: "10am", crew: "A", addr: "445 Springmill Rd · Ontario · SFR × 3",          svc: "Turn-Over Cleanup · Between Tenants" },
        { id: "a4",  day: "tue", time: "2pm",  crew: "B", addr: "1922 Lexington Ave · Mansfield · Triplex",       svc: "Gutter Clear + Fall Debris Haul" },
        { id: "a5",  day: "wed", time: "8am",  crew: "A", addr: "618 Kelton Ave · Bexley · SFR",                  svc: "Weekly Mow + Bed Detail" },
        { id: "a6",  day: "wed", time: "12pm", crew: "B", addr: "704 W 4th St · Marion · Duplex",                 svc: "Fence Line Trim + Brush Cut" },
        { id: "a7",  day: "thu", time: "10am", crew: "A", addr: "2201 Lexington-Springmill · Portfolio",          svc: "Seasonal Aeration + Overseed" },
        { id: "a8",  day: "thu", time: "4pm",  crew: "B", addr: "133 Park Ave West · Mansfield · Rental",         svc: "Emergency Storm Debris Cleanup" },
        { id: "a9",  day: "fri", time: "8am",  crew: "A", addr: "3308 N High St · Columbus · Multi-Unit",         svc: "Weekly Mow · 6-Unit Property" },
        { id: "a10", day: "fri", time: "2pm",  crew: "B", addr: "710 N Diamond St · Mansfield · SFR",             svc: "Winter Prep · Downspout + Bed Cutback" },
      ],
    },
    spine: {
      kicker: "Module 03 · Utility Spine · Property Vault",
      title: "Every property, every access note, remembered.",
      blurb:
        "Search 'back gate combo' or 'sprinkler shutoff' across every property on file. Crews add access notes voice-to-text from the truck. Nothing gets forgotten between visits.",
      addAction: "Add Property Note",
      newFileLabel: "New Property File",
      entityLabel: "Property Owner",
      addrLabel: "Property Address · Unit Count",
      counterLabel: "Property files",
      files: [
        { id: "af1", name: "Halstead Holdings LLC",    addr: "3120 Trimble Rd · Mansfield · Duplex (2u)",           last: "Jan 27 · Weekly Mow",           next: "Feb 03 · Weekly Mow + Edge",           note: "Gate combo 4-4-1-9. Do NOT prop open — neighbor's dog gets out. Recurring weekly plan through Nov 15. Owner Karen prefers text over call." },
        { id: "af2", name: "Vance Rental Group",       addr: "812 N Main St · Mansfield · Fourplex (4u)",           last: "Jan 24 · Hedge Trim",           next: "Feb 03 · Hedge + Bed Weed",            note: "Tenants asked crew to knock before hedge trim on the north side — Unit C works nights. Bed line runs 42 ft. Leave clippings at the far dumpster, NOT curbside." },
        { id: "af3", name: "Ontario Homes Investment", addr: "445 Springmill Rd · Ontario · SFR × 3",               last: "Dec 15 · Fall Cleanup",         next: "Feb 04 · Turn-Over Cleanup",           note: "Between tenants on the middle SFR — full property reset. Owner wants photos of every corner before/after. Trash pull included. Turn-over rate $340 per unit." },
        { id: "af4", name: "Lexington Ave Partners",   addr: "1922 Lexington Ave · Mansfield · Triplex (3u)",       last: "Nov 12 · Gutter Clear",         next: "Feb 04 · Gutter Clear + Haul",         note: "Second gutter clear this season — heavy maple canopy. Owner wants the downspout at the SE corner scoped for the blockage caught last visit. Roof access via garage ladder only." },
        { id: "af5", name: "Buckeye Property Trust",   addr: "3308 N High St · Columbus · Multi-Unit (6u)",         last: "Jan 30 · Weekly Mow",           next: "Feb 05 · Weekly Mow · 6-Unit",         note: "6-unit portfolio · 0.9 acre common lawn + individual bed lines. Sprinkler shutoff behind Unit 4 utility panel. Locked electrical closet — key at leasing office M–F 9-5." },
      ],
    },
    billing: {
      kicker: "Module 04 · Billing Autopilot · Recurring Service",
      title: "Service complete → Recurring invoice sent. One button.",
      blurb:
        "The moment the crew rolls off the property, tap Send Invoice. Amanda posts the recurring bill to the owner's portal, texts a pay-link, and files the work order in the Grid. Monthly statements auto-generated on the 1st.",
      entityLabel: "Property Owner",
      openLabel: "Work orders waiting",
      invoicedLabel: "Invoiced today",
      billedLabel: "Billed today",
      invoices: [
        { id: "inv1", addr: "3120 Trimble Rd · Halstead Holdings",       svc: "Weekly Mow + Edge · 0.4 acre",             crew: "Mow Crew",         date: "Feb 03", price: 95   },
        { id: "inv2", addr: "812 N Main St · Vance Rental Group",        svc: "Hedge Trim + Bed Weed · Common Area",      crew: "Maintenance Crew", date: "Feb 03", price: 280  },
        { id: "inv3", addr: "445 Springmill Rd · Ontario Homes",         svc: "Turn-Over Cleanup · SFR (middle unit)",    crew: "Maintenance Crew", date: "Feb 04", price: 340  },
        { id: "inv4", addr: "1922 Lexington Ave · Lexington Partners",   svc: "Gutter Clear + Fall Debris Haul",          crew: "Maintenance Crew", date: "Feb 04", price: 425  },
        { id: "inv5", addr: "3308 N High St · Buckeye Property Trust",   svc: "Weekly Mow · 6-Unit Property · 0.9 acre",  crew: "Mow Crew",         date: "Feb 05", price: 260  },
      ],
    },
    growth: {
      kicker: "Module 05 · Growth Engine · Property Reveal",
      title: "Ten cinematic property transformations a week — Amanda drafts them all.",
      blurb:
        "Amanda tracks the local property market for high-engagement themes. Turn-over reveals, portfolio walk-throughs, seasonal service demos, and owner testimonials — auto-drafted, scheduled, and posted.",
      pill: "10 Cinematic Property Transformations/Week",
      videoShowcase: {
        caption: "This is what your 10 weekly assets look like.",
        videos: [
          {
            src: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/6b1abec8d3f08ccd.mp4",
            label: "The Before & After — Property Transformation",
          },
          {
            src: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/eab639f3475aeaa7.mp4",
            label: "The Process — Professional Crew in Action",
          },
        ],
      },
      videos: [
        { day: "Mon", slot: "AM", type: "Transformation",    title: "Overgrown Rental → Turn-Over Ready in 6 Hours" },
        { day: "Mon", slot: "PM", type: "Seasonal Push",     title: "Winter Property Prep · 8 Portfolios Left" },
        { day: "Tue", slot: "AM", type: "Crew Spotlight",    title: "Meet Ray · Lead on the Maintenance Crew" },
        { day: "Wed", slot: "AM", type: "Owner Story",       title: "Vance Rental Group · 4 Years on the Grid" },
        { day: "Wed", slot: "PM", type: "Portfolio Walk",    title: "6-Unit Multi-Family · Weekly Mow Route" },
        { day: "Thu", slot: "AM", type: "Quick Tip",         title: "60-Second Gutter-Blockage Diagnostic" },
        { day: "Fri", slot: "AM", type: "Turn-Over Reel",    title: "SFR Turn-Over · Springmill Rd · Timelapse" },
        { day: "Fri", slot: "PM", type: "Team Reel",         title: "This Week Across the Portfolios" },
        { day: "Sat", slot: "AM", type: "Seasonal Push",     title: "Aeration + Overseed · 5 Slots Left" },
        { day: "Sun", slot: "PM", type: "Testimonial",       title: "Property Owner of the Month · Ontario Homes" },
      ],
    },
  },
};
